import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { solutionsDetails, processSteps } from "@/mocks/solutions";
import { solutionsDetailData } from "@/mocks/solutions-detail";

export default function Solutions() {
  const getSlug = (id: number) => solutionsDetailData.find((s) => s.id === id)?.slug || "#";
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-20 bg-primary relative overflow-hidden" style={{ minHeight: "380px" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Our Solutions</h1>
            <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base">
              End-to-end audio-visual and smart technology solutions for every industry.
            </p>
            <div className="w-16 h-1 bg-accent mx-auto mt-6 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-12 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className="space-y-12 md:space-y-16">
            {solutionsDetails.map((solution, index) => (
              <motion.div
                key={solution.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center`}
              >
                <Link to={`/solutions/${getSlug(solution.id)}`} className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                      <i className={`${solution.icon} text-accent text-xl`} />
                    </div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900 hover:text-accent transition-colors">{solution.title}</h2>
                      <p className="text-xs text-accent font-semibold uppercase tracking-wide">{solution.shortDesc}</p>
                    </div>
                  </div>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">{solution.description}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {solution.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-accent flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-accent text-sm font-medium whitespace-nowrap">
                    Learn more
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
                <Link to={`/solutions/${getSlug(solution.id)}`} className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="rounded-2xl overflow-hidden border border-gray-100 bg-gray-50 cursor-pointer"
                  >
                    <div className="aspect-video w-full overflow-hidden">
                      <img
                        src={solution.image}
                        alt={solution.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top"
                        width={800}
                        height={450}
                      />
                    </div>
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Our Process</h2>
            <div className="w-16 h-1 bg-accent mx-auto rounded-full" />
            <p className="text-sm text-gray-500 mt-4 max-w-xl mx-auto">
              A proven five-step methodology that delivers consistent excellence on every project.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                className="p-6 rounded-xl bg-white border border-gray-100 hover:border-accent/30 hover:shadow-md transition-all duration-300 text-center"
              >
                <div className="text-3xl font-bold text-accent/30 mb-3">{step.step}</div>
                <h3 className="text-base font-bold text-gray-800 mb-2">{step.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}